"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Button, Section } from "@/src/components";
import {
  LocaleProvider,
  useLocale,
  useIsRtl,
  useNumberFormat,
  useDateFormat,
  usePluralize,
  formatNumber,
  formatCurrency,
  formatCompact,
  formatDate,
  formatRelativeTime,
  getRelativeTime,
  formatList,
  getDisplayName,
} from "@/lib/i18n";
import { SelectBlock } from "@/src/components/input/select-advanced";

const localeOptions = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "de-DE", label: "German (Germany)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "ja-JP", label: "Japanese (Japan)" },
  { value: "ar-SA", label: "Arabic (Saudi Arabia)" },
  { value: "he-IL", label: "Hebrew (Israel)" },
  { value: "zh-CN", label: "Chinese (Simplified)" },
];

function LocaleDemo() {
  const { locale, dir } = useLocale();
  const isRtl = useIsRtl();
  const numberFormat = useNumberFormat();
  const dateFormat = useDateFormat();
  const pluralize = usePluralize();

  const now = new Date();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const sampleNumber = 1234567.89;

  return (
    <div className="space-y-6">
      {/* Current Locale Info */}
      <div className="flex items-center gap-4 p-4 bg-accent/30 rounded-lg">
        <div>
          <Text className="text-xs text-muted-foreground">Locale</Text>
          <Text className="font-mono">{locale}</Text>
        </div>
        <div>
          <Text className="text-xs text-muted-foreground">Direction</Text>
          <Badge variant={isRtl ? "default" : "secondary"}>{dir.toUpperCase()}</Badge>
        </div>
        <div>
          <Text className="text-xs text-muted-foreground">Language Name</Text>
          <Text>{getDisplayName(locale.split("-")[0], "language", locale)}</Text>
        </div>
      </div>

      {/* Number Formatting */}
      <div className="space-y-3">
        <Text className="font-medium">Number Formatting</Text>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Standard</Text>
            <Text className="font-mono">{numberFormat.format(sampleNumber)}</Text>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Currency</Text>
            <Text className="font-mono">{numberFormat.formatCurrency(99.99, "USD")}</Text>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Percent</Text>
            <Text className="font-mono">{numberFormat.formatPercent(0.1234)}</Text>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Compact</Text>
            <Text className="font-mono">{numberFormat.formatCompact(1234567)}</Text>
          </div>
        </div>
      </div>

      {/* Date Formatting */}
      <div className="space-y-3">
        <Text className="font-medium">Date Formatting</Text>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Date</Text>
            <Text className="font-mono">{dateFormat.format(now)}</Text>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Date + Time</Text>
            <Text className="font-mono">{dateFormat.formatDateTime(now)}</Text>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Time</Text>
            <Text className="font-mono">{dateFormat.formatTime(now)}</Text>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <Text className="text-xs text-muted-foreground mb-1">Relative</Text>
            <Text className="font-mono">{dateFormat.formatRelative(yesterday)}</Text>
          </div>
        </div>
      </div>

      {/* Pluralization */}
      <div className="space-y-3">
        <Text className="font-medium">Pluralization</Text>
        <div className="flex gap-4 text-sm">
          {[0, 1, 2, 5].map((count) => (
            <div key={count} className="p-3 bg-muted/30 rounded-lg">
              <Text className="text-xs text-muted-foreground mb-1">{count}</Text>
              <Text className="font-mono">
                {count} {pluralize(count, { one: "item", other: "items" })}
              </Text>
            </div>
          ))}
        </div>
      </div>

      {/* List Formatting */}
      <div className="space-y-3">
        <Text className="font-medium">List Formatting</Text>
        <div className="p-3 bg-muted/30 rounded-lg text-sm">
          <Text className="font-mono">
            {formatList(["Apple", "Orange", "Banana"], locale)}
          </Text>
        </div>
      </div>

      {/* RTL Demo */}
      {isRtl && (
        <div className="space-y-3">
          <Text className="font-medium">RTL Layout Demo</Text>
          <div className="flex gap-4 p-4 border rounded-lg">
            <div className="flex-1 p-3 bg-primary/10 rounded text-center">Start</div>
            <div className="flex-1 p-3 bg-muted rounded text-center">Middle</div>
            <div className="flex-1 p-3 bg-primary/10 rounded text-center">End</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocsI18n() {
  const [locale, setLocale] = React.useState("en-US");

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Internationalization (i18n)
            </Text>
            <Badge variant="secondary">Phase 10</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Built-in support for RTL layouts, number/date formatting, and
            localization using the native Intl API.
          </Text>
        </div>
      </Section>

      {/* Locale Selector */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Interactive Demo</CardTitle>
            <CardDescription>Switch locales to see formatting changes</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="mb-6">
          <Text className="text-sm font-medium mb-2">Select Locale</Text>
          <SelectBlock
            options={localeOptions}
            value={localeOptions.find((o) => o.value === locale) || null}
            onChange={(option) => setLocale((option as typeof localeOptions[0])?.value || "en-US")}
            placeholder="Select locale..."
          />
        </div>

        <LocaleProvider locale={locale}>
          <LocaleDemo />
        </LocaleProvider>
      </SectionCard>
      </Section>

      {/* RTL Support */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>RTL Support</CardTitle>
            <CardDescription>Use CSS logical properties for automatic RTL</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <Text className="text-sm text-muted-foreground">
          The design system uses CSS logical properties for automatic RTL support.
          Instead of physical properties like <code className="bg-muted px-1 rounded">margin-left</code>,
          use logical equivalents:
        </Text>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">Physical (avoid)</th>
                <th className="text-left py-2 pr-4 font-medium">Logical (use)</th>
                <th className="text-left py-2 font-medium">CSS Class</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">margin-left</td>
                <td className="py-2 pr-4 font-mono text-xs">margin-inline-start</td>
                <td className="py-2 font-mono text-xs">.ms-4</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">margin-right</td>
                <td className="py-2 pr-4 font-mono text-xs">margin-inline-end</td>
                <td className="py-2 font-mono text-xs">.me-4</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">padding-left</td>
                <td className="py-2 pr-4 font-mono text-xs">padding-inline-start</td>
                <td className="py-2 font-mono text-xs">.ps-4</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">padding-right</td>
                <td className="py-2 pr-4 font-mono text-xs">padding-inline-end</td>
                <td className="py-2 font-mono text-xs">.pe-4</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">left</td>
                <td className="py-2 pr-4 font-mono text-xs">inset-inline-start</td>
                <td className="py-2 font-mono text-xs">.start-0</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">right</td>
                <td className="py-2 pr-4 font-mono text-xs">inset-inline-end</td>
                <td className="py-2 font-mono text-xs">.end-0</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">text-align: left</td>
                <td className="py-2 pr-4 font-mono text-xs">text-align: start</td>
                <td className="py-2 font-mono text-xs">.text-start</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground line-through">text-align: right</td>
                <td className="py-2 pr-4 font-mono text-xs">text-align: end</td>
                <td className="py-2 font-mono text-xs">.text-end</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
          <code>{`/* For directional icons like arrows, use: */
<ChevronRight className="rtl-flip" />

/* This flips the icon horizontally in RTL mode */`}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Number Formatting */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Number Formatting</CardTitle>
            <CardDescription>Format numbers according to locale</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto space-y-2">
          <code className="block">{"// Standard number"}</code>
          <code className="block">{"formatNumber(1234567.89, \"en-US\"); // \"1,234,567.89\""}</code>
          <code className="block">{"formatNumber(1234567.89, \"de-DE\"); // \"1.234.567,89\""}</code>
          <code className="block mt-4">{"// Currency"}</code>
          <code className="block">{"formatCurrency(99.99, \"en-US\", \"USD\"); // \"$99.99\""}</code>
          <code className="block">{"formatCurrency(99.99, \"de-DE\", \"EUR\"); // \"99,99 €\""}</code>
          <code className="block mt-4">{"// Compact"}</code>
          <code className="block">{"formatCompact(1234567, \"en-US\"); // \"1.2M\""}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Date Formatting */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Date & Time Formatting</CardTitle>
            <CardDescription>Format dates with Intl.DateTimeFormat</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto space-y-2">
          <code className="block">{"// Date only"}</code>
          <code className="block">{"formatDate(new Date(), \"en-US\"); // \"1/17/2026\""}</code>
          <code className="block">{"formatDate(new Date(), \"de-DE\"); // \"17.1.2026\""}</code>
          <code className="block mt-4">{"// Relative time"}</code>
          <code className="block">{"getRelativeTime(yesterday, \"en-US\"); // \"yesterday\""}</code>
          <code className="block">{"formatRelativeTime(-2, \"hours\", \"en-US\"); // \"2 hours ago\""}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Hooks */}
      <Section>
        <SectionCard
        header={<CardTitle>Available Hooks</CardTitle>}
        contentClassName="p-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">Hook</th>
                <th className="text-left py-2 font-medium">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useLocale()</td>
                <td className="py-2 text-muted-foreground">Get current locale config</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useIsRtl()</td>
                <td className="py-2 text-muted-foreground">Check if current locale is RTL</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useNumberFormat()</td>
                <td className="py-2 text-muted-foreground">Number formatting with current locale</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">useDateFormat()</td>
                <td className="py-2 text-muted-foreground">Date formatting with current locale</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">usePluralize()</td>
                <td className="py-2 text-muted-foreground">Pluralization with current locale</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </Section>

      {/* Setup */}
      <Section>
        <SectionCard
        header={<CardTitle>Setup</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
          <code>{`// In your root layout or app component:
import { LocaleProvider } from "@/lib/i18n";

export default function RootLayout({ children }) {
  return (
    <LocaleProvider locale="en-US">
      {children}
    </LocaleProvider>
  );
}

// For RTL locales:
<LocaleProvider locale="ar-SA" dir="rtl">
  {children}
</LocaleProvider>`}</code>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
          <code>{`// Import utilities:
import {
  LocaleProvider,
  useLocale,
  useIsRtl,
  useNumberFormat,
  useDateFormat,
  formatNumber,
  formatCurrency,
  formatDate,
  getRelativeTime,
} from "@/lib/i18n";`}</code>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
